import { Injectable } from '@angular/core';
import { FormArray, FormBuilder, FormControl } from '@angular/forms';
import { Subscription } from 'rxjs';

export interface NamedId {
  id: string;
  name: string;
}

export interface SelectionState {
  allSelected: boolean;
  selectedCount: number;
}

@Injectable({
  providedIn: 'root',
})
export class SelectionService {
  constructor(private fb: FormBuilder) {}

  /**
   * Creates form controls for items based on selection state
   */
  createItemFormControls(items: NamedId[], selectedIds: Set<string>): FormArray {
    const controls = items.map((item) => this.fb.control(selectedIds.has(item.id)));
    return this.fb.array(controls);
  }

  /**
   * Toggles select all functionality
   */
  toggleSelectAll(formArray: FormArray, items: NamedId[], selectedIds: Set<string>): void {
    const checkedCount = formArray.controls.filter((c) => c.value).length;

    if (checkedCount === 0) {
      // If no items are checked, check all
      formArray.controls.forEach((control, index) => {
        control.setValue(true);
        selectedIds.add(items[index].id);
      });
    } else {
      // If some items are checked, uncheck all
      formArray.controls.forEach((control, index) => {
        control.setValue(false)
        selectedIds.delete(items[index].id);
      });
    }
  }

  /**
   * Gets the current selection state
   */
  getSelectionState(formArray: FormArray): SelectionState {
    const checkedCount = formArray.controls.filter((c) => c.value).length;
    const itemsLength = formArray.length;

    return {
      allSelected: checkedCount === itemsLength && itemsLength > 0,
      selectedCount: checkedCount,
    };
  }

  /**
   * Updates the select all control state based on current selection
   */
  updateSelectAllControl(
    selectAllControl: FormControl<boolean | null>,
    formArray: FormArray,
  ): void {
    const state = this.getSelectionState(formArray);

    if (state.allSelected) {
      selectAllControl.setValue(true);
    } else if (state.selectedCount === 0) {
      selectAllControl.setValue(false);
    } else {
      selectAllControl.setValue(null); // Indeterminate state
    }
  }

  /**
   * Sets up subscriptions to track individual item selection changes
   */
  trackSelectionChanges(
    formArray: FormArray,
    items: NamedId[],
    selectedIds: Set<string>,
    onSelectionChange: (selectedIds: Set<string>) => void,
  ): Subscription[] {
    const subscriptions: Subscription[] = [];

    formArray.controls.forEach((control, index) => {
      const id = items[index].id;
      const sub = control.valueChanges.subscribe((checked: boolean) => {
        if (checked) {
          selectedIds.add(id);
        } else {
          selectedIds.delete(id);
        }
        onSelectionChange(selectedIds);
      });
      subscriptions.push(sub);
    });

    return subscriptions;
  }

  /**
   * Finds differences between two arrays (utility for tracking changes)
   */
  getDiffIndices(prev: any[], curr: any[]): { value: boolean; idx?: number } | null {
    for (let i = 0; i < Math.max(prev.length, curr.length); i++) {
      if (prev[i] !== curr[i]) {
        return { value: curr[i], idx: i };
      }
    }
    return null;
  }

  /**
   * Gets member info from diff result
   */
  getMemberInfo(
    diff: { value: boolean; idx?: number },
    items: NamedId[],
  ): { id: string; name: string } {
    if (diff.idx !== undefined) {
      const item = items[diff.idx];
      return { id: item.id, name: item.name };
    }
    throw new Error('Invalid diff object');
  }

  /**
   * Cleans up selection subscriptions
   */
  unsubscribeSelections(subscriptions: Subscription[]): void {
    subscriptions.forEach((sub) => sub.unsubscribe());
    subscriptions.length = 0;
  }
}
